/**
 * Formula Evaluator for Glossary Stat Formulas
 * 
 * Evaluates formulas like "Mind + Mind + Body" or "Body - Reflex + 3"
 * Used to calculate skill checks and derived stats from core stats.
 */

/**
 * Result of formula evaluation with validation info
 */
export interface FormulaEvaluationResult {
  result: number;
  isValid: boolean;
}

/**
 * Evaluates a formula string against a map of stat values with validation.
 * Supports addition (+) and subtraction (-) operators.
 * 
 * @param formula - Formula string like "Mind + Mind + Body" or "Body - Reflex + 3"
 * @param stats - Record of stat names to values, e.g., { Mind: 2, Body: 3, Reflex: 2 }
 * @returns Object with result and isValid flag (false if any unknown stat names encountered)
 * 
 * @example
 * evaluateFormulaWithValidation("Mind + Mind + Body", { Mind: 2, Body: 3, Reflex: 2 })
 * // Returns: { result: 7, isValid: true }
 * 
 * @example
 * evaluateFormulaWithValidation("Cost 2 Resilience", { Mind: 2, Body: 3 })
 * // Returns: { result: 2, isValid: false } (Cost and Resilience are unknown)
 */
export function evaluateFormulaWithValidation(
  formula: string,
  stats: Record<string, number>
): FormulaEvaluationResult {
  if (!formula || formula.trim() === '') {
    return { result: 0, isValid: true };
  }

  // Normalize the stats object to be case-insensitive
  const normalizedStats: Record<string, number> = {};
  for (const [key, value] of Object.entries(stats)) {
    normalizedStats[key.toLowerCase()] = value;
  }

  // Tokenize the formula into parts with their operators
  // Match patterns: optional +/- followed by a term (stat name or number)
  const tokenRegex = /([+-]?)\s*([^+-]+)/g;
  const tokens: Array<{ operator: '+' | '-'; value: string }> = [];

  let match;
  while ((match = tokenRegex.exec(formula)) !== null) {
    const operator = match[1] === '-' ? '-' : '+';
    const value = match[2].trim();
    if (value) {
      tokens.push({ operator, value });
    }
  }

  let sum = 0;
  let isValid = true;

  for (const token of tokens) {
    // Try to parse as a number first
    const numValue = Number(token.value);
    if (!isNaN(numValue)) {
      sum += token.operator === '+' ? numValue : -numValue;
      continue;
    }

    // Otherwise, treat as a stat name (case-insensitive lookup)
    const statValue = normalizedStats[token.value.toLowerCase()];
    if (statValue !== undefined) {
      sum += token.operator === '+' ? statValue : -statValue;
    } else {
      // Mark as invalid if stat not found
      isValid = false;
      // Warn about unknown stats to help catch formula typos during development
      // eslint-disable-next-line no-console
      console.warn(`Unknown stat or value in formula: "${token.value}" in formula "${formula}"`);
    }
  }

  return { result: sum, isValid };
}

/**
 * Evaluates a formula string against a map of stat values.
 * Supports addition (+) and subtraction (-) operators.
 * 
 * @param formula - Formula string like "Mind + Mind + Body" or "Body - Reflex + 3"
 * @param stats - Record of stat names to values, e.g., { Mind: 2, Body: 3, Reflex: 2 }
 * @returns The calculated result
 * 
 * @example
 * evaluateFormula("Mind + Mind + Body", { Mind: 2, Body: 3, Reflex: 2 })
 * // Returns: 7 (2 + 2 + 3)
 * 
 * @example
 * evaluateFormula("Body - Reflex + 3", { Mind: 2, Body: 3, Reflex: 2 })
 * // Returns: 4 (3 - 2 + 3)
 */
export function evaluateFormula(
  formula: string,
  stats: Record<string, number>
): number {
  return evaluateFormulaWithValidation(formula, stats).result;
}

/**
 * Extracts the stat names used in a formula (for dependency tracking).
 * 
 * @param formula - Formula string like "Mind + Mind + Body"
 * @returns Array of unique stat names referenced in the formula
 * 
 * @example
 * getFormulaStatDependencies("Mind + Mind + Body")
 * // Returns: ["Mind", "Body"]
 * 
 * @example
 * getFormulaStatDependencies("Body + Body + 3")
 * // Returns: ["Body"]
 */
export function getFormulaStatDependencies(formula: string): string[] {
  if (!formula || formula.trim() === '') {
    return [];
  }

  const parts = formula.split('+').map(part => part.trim());
  const statNames = new Set<string>();

  for (const part of parts) {
    // Skip if it's a number
    if (!isNaN(Number(part))) {
      continue;
    }

    // Add stat name (preserve original case)
    statNames.add(part);
  }

  return Array.from(statNames);
}
